import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { Skeleton } from './Skeleton';

interface LoadingStateProps {
  message?: string;
  showLongLoadingMessage?: boolean;
  longLoadingThreshold?: number;
  longLoadingMessage?: string;
  onCancel?: () => void;
  className?: string;
  variant?: 'spinner' | 'dots' | 'pulse';
  size?: 'sm' | 'md' | 'lg';
}

export function LoadingState({
  message = 'Loading...',
  showLongLoadingMessage = true,
  longLoadingThreshold = 3000,
  longLoadingMessage = 'This is taking longer than expected...',
  onCancel,
  className,
  variant = 'spinner',
  size = 'md',
}: LoadingStateProps) {
  const [isLongLoading, setIsLongLoading] = useState(false);

  useEffect(() => {
    if (showLongLoadingMessage) {
      const timer = setTimeout(() => setIsLongLoading(true), longLoadingThreshold);
      return () => clearTimeout(timer);
    }
  }, [showLongLoadingMessage, longLoadingThreshold]);

  const sizeClasses = {
    sm: { spinner: 'h-5 w-5', text: 'text-xs' },
    md: { spinner: 'h-8 w-8', text: 'text-sm' },
    lg: { spinner: 'h-12 w-12', text: 'text-base' },
  };

  const renderLoader = () => {
    switch (variant) {
      case 'dots':
        return (
          <div className="flex gap-1">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className={clsx(
                  'rounded-full bg-amber-600',
                  size === 'sm' ? 'h-1.5 w-1.5' : size === 'lg' ? 'h-3 w-3' : 'h-2 w-2'
                )}
                style={{
                  animation: 'pulse 1.4s ease-in-out infinite',
                  animationDelay: `${i * 0.16}s`,
                }}
              />
            ))}
          </div>
        );
      case 'pulse':
        return (
          <div
            className={clsx(
              'rounded-full bg-amber-600/20',
              sizeClasses[size].spinner
            )}
            style={{ animation: 'pulse 2s ease-in-out infinite' }}
          >
            <div className="h-full w-full rounded-full bg-amber-600/40 animate-ping" />
          </div>
        );
      default:
        return (
          <div
            className={clsx(
              'animate-spin rounded-full border-4 border-amber-600 border-t-transparent',
              sizeClasses[size].spinner
            )}
          />
        );
    }
  };

  return (
    <div
      className={clsx('flex flex-col items-center justify-center p-8', className)}
      role="status"
      aria-live="polite"
    >
      {renderLoader()}
      <p className={clsx('mt-3 text-gray-600 font-medium', sizeClasses[size].text)}>
        {message}
      </p>
      {isLongLoading && (
        <p className="mt-1 text-xs text-gray-400">{longLoadingMessage}</p>
      )}
      {isLongLoading && onCancel && (
        <button
          onClick={onCancel}
          className="mt-3 text-sm text-amber-600 hover:text-amber-700 font-medium"
        >
          Cancel and retry
        </button>
      )}
    </div>
  );
}

// Pre-built skeleton layouts for validation pages
export function ValidationDashboardSkeleton() {
  return (
    <div className="space-y-6">
      {/* Header skeleton */}
      <div className="flex items-center justify-between">
        <div>
          <Skeleton width={280} height={28} className="mb-2" />
          <Skeleton width={400} height={16} />
        </div>
        <div className="flex gap-3">
          <Skeleton width={140} height={40} variant="rectangular" />
          <Skeleton width={140} height={40} variant="rectangular" />
        </div>
      </div>

      {/* Stats grid skeleton */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <Skeleton width={80} height={14} />
                <Skeleton width={60} height={28} />
                <Skeleton width={100} height={12} />
              </div>
              <Skeleton width={48} height={48} variant="rectangular" className="rounded-lg" />
            </div>
          </div>
        ))}
      </div>

      {/* Content grid skeleton */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {[1, 2].map((i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="border-b border-gray-100 px-5 py-4">
              <Skeleton width={160} height={18} />
            </div>
            <div className="p-5 space-y-4">
              {Array.from({ length: 4 }).map((_, j) => (
                <div key={j} className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Skeleton width={180} height={14} />
                    <Skeleton width={120} height={12} />
                  </div>
                  <Skeleton width={80} height={24} variant="rectangular" />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function TestCaseListSkeleton({ rows = 8 }: { rows?: number }) {
  return (
    <div className="space-y-4">
      {/* Filter bar skeleton */}
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <Skeleton width={250} height={40} variant="rectangular" className="flex-1 min-w-[200px]" />
        <Skeleton width={140} height={40} variant="rectangular" />
        <Skeleton width={140} height={40} variant="rectangular" />
        <Skeleton width={140} height={40} variant="rectangular" />
      </div>

      {/* Table skeleton */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className="px-4 py-3 text-left"><Skeleton width={80} height={12} /></th>
              <th className="px-4 py-3 text-left hidden md:table-cell"><Skeleton width={60} height={12} /></th>
              <th className="px-4 py-3 text-left hidden lg:table-cell"><Skeleton width={80} height={12} /></th>
              <th className="px-4 py-3 text-left"><Skeleton width={50} height={12} /></th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {Array.from({ length: rows }).map((_, i) => (
              <tr key={i}>
                <td className="px-4 py-3">
                  <div className="space-y-1">
                    <Skeleton width="80%" height={14} />
                    <Skeleton width="60%" height={12} />
                  </div>
                </td>
                <td className="px-4 py-3 hidden md:table-cell">
                  <Skeleton width={100} height={24} variant="rectangular" />
                </td>
                <td className="px-4 py-3 hidden lg:table-cell">
                  <Skeleton width={80} height={24} variant="rectangular" />
                </td>
                <td className="px-4 py-3">
                  <Skeleton width={70} height={24} variant="rectangular" />
                </td>
                <td className="px-4 py-3">
                  <Skeleton width={32} height={32} variant="rectangular" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AnalyticsSkeleton() {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Skeleton width={160} height={28} className="mb-2" />
          <Skeleton width={240} height={16} />
        </div>
        <div className="flex items-center gap-3">
          <Skeleton width={200} height={36} variant="rectangular" />
          <Skeleton width={120} height={36} variant="rectangular" />
        </div>
      </div>

      {/* Model cards */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <Skeleton width={120} height={20} />
              <Skeleton width={60} height={24} variant="rectangular" />
            </div>
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, j) => (
                <div key={j} className="flex justify-between">
                  <Skeleton width={100} height={14} />
                  <Skeleton width={40} height={14} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Category breakdown */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <Skeleton width={200} height={20} className="mb-4" />
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4">
              <Skeleton width={120} height={14} />
              <Skeleton className="flex-1" height={24} variant="rectangular" />
              <Skeleton width={40} height={14} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
